import React, { useEffect, useMemo, useState } from 'react'
import { TextStyle } from 'react-native'

import { BaseText } from '../../atoms'
import { SubheadSize, TextSize, TextType } from '../../styles/typeStyles'

export enum TypewriterDisplayType {
  LETTER = 'letter',
  WORD = 'word',
}

export interface TypewriterTextProps {
  bold?: boolean
  delayInMilliseconds?: number
  loop?: boolean
  millisecondsPerLetter?: number
  textArray: Array<string>
  textSize?: TextSize
  textStyle?: TextStyle
  type: TextType
  accessibilityLabel?: string
  displayType?: TypewriterDisplayType
  indexToCSSOverride?: Record<number, TextStyle>
}

const DEFAULT_SPEED = 80
const DEFAULT_DELAY = 1000

export const TypewriterText: React.FC<TypewriterTextProps> = ({
  bold = false,
  delayInMilliseconds = DEFAULT_DELAY,
  loop = true,
  millisecondsPerLetter = DEFAULT_SPEED,
  textArray,
  textSize = SubheadSize.MEDIUM,
  textStyle,
  type,
  accessibilityLabel,
  displayType = TypewriterDisplayType.LETTER,
  indexToCSSOverride = undefined,
}) => {
  const [stringIndex, setStringIndex] = useState(0) // Start at the first word in the textArray
  const [unitIndex, setUnitIndex] = useState(0)

  const currentText = textArray[stringIndex] || ''
  const words = useMemo(
    () => (displayType === TypewriterDisplayType.WORD ? (currentText ? currentText.split(/ +/) : []) : []),
    [currentText, displayType],
  )
  const totalUnits = displayType === TypewriterDisplayType.WORD ? words.length : currentText.length

  useEffect(() => {
    if (totalUnits <= 0) return

    const display = setTimeout(() => {
      if (unitIndex < totalUnits - 1) {
        setUnitIndex(unitIndex + 1)
      } else {
        if (stringIndex < textArray.length - 1) {
          setTimeout(() => {
            setUnitIndex(0)
            setStringIndex(stringIndex + 1)
          }, delayInMilliseconds)
        } else {
          if (loop) {
            setTimeout(() => {
              setUnitIndex(0)
              setStringIndex(0)
            }, delayInMilliseconds)
          }
        }
      }
    }, millisecondsPerLetter)
    return () => clearTimeout(display)
  }, [delayInMilliseconds, loop, millisecondsPerLetter, stringIndex, textArray, totalUnits, unitIndex])

  const displayedText =
    displayType === TypewriterDisplayType.WORD
      ? words.slice(0, unitIndex + 1).join(' ') || ''
      : currentText.substring(0, Math.max(0, unitIndex + 1))

  return (
    <BaseText
      {...(bold && { bold })}
      text={displayedText}
      size={textSize}
      style={{ ...textStyle, ...(indexToCSSOverride ? indexToCSSOverride[stringIndex] : {}) }}
      type={type}
      accessibilityLabel={accessibilityLabel}
    />
  )
}
